/**
 * TimesheetDrawer Component
 * Create / Edit weekly timesheet with 7-day grid
 * BuilderBel Construction Project Management System
 */

import { AlertCircle, Clock, Trash2, X } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useToast } from '../../contexts/ToastContext';
import { useCurrentProjectId } from '../../hooks/useProjectRedux';
import { useCreateCostCodeMutation, useGetCostCodesQuery } from '../../services/costCodeApi';
import {
  useGetProjectInvitationsQuery,
  useGetProjectTeamQuery,
  useSendInvitationsMutation,
} from '../../services/teamApi';
import {
  useCreateTimesheetMutation,
  useUpdateTimesheetMutation,
} from '../../services/timesheetApi';
import type { CostCode } from '../../types/costCode';
import type { CreateTimesheetEntryRequest, Timesheet } from '../../types/timesheet';
import { DatePicker, Input, NumberInput, SearchableDropdown, TimePicker } from '../forms';

// ============================================================================
// CONSTANTS
// ============================================================================

const TEAM_ROLE_OPTIONS = [
  { value: 'contractor', label: 'Contractor' },
  { value: 'site_supervisor', label: 'Site Supervisor' },
  { value: 'engineer', label: 'Engineer' },
  { value: 'architect', label: 'Architect' },
  { value: 'project_manager', label: 'Project Manager' },
  { value: 'client', label: 'Client' },
  { value: 'viewer', label: 'Viewer' },
];

// ============================================================================
// TYPES
// ============================================================================

export interface TimesheetDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  timesheet?: Timesheet | null;
  mode: 'create' | 'edit' | 'view';
  onSuccess?: () => void;
  teamMembers?: { _id: string; name: string; email: string; role?: string }[];
}

interface DayEntry {
  date: string; // ISO date string
  dayLabel: string; // e.g. "Mon 10"
  startTime: string; // HH:mm
  endTime: string; // HH:mm
  breakMinutes: number;
  hoursWorked: number;
  costCode: string;
  costCodeDescription: string;
  notes: string;
}

// ============================================================================
// HELPERS
// ============================================================================

/** Get the Monday of the week containing the given date. */
function getMondayOfWeek(date: Date): Date {
  const d = new Date(date);
  const day = d.getDay(); // 0=Sun, 1=Mon, ...
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

/** Build an array of 7 dates (Mon–Sun) for a given Monday. */
function buildWeekDays(monday: Date): { date: Date; label: string }[] {
  const days: { date: Date; label: string }[] = [];
  const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  for (let i = 0; i < 7; i++) {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    days.push({
      date: d,
      label: `${DAY_LABELS[i]} ${d.getDate()}`,
    });
  }
  return days;
}

/** Calculate hours from HH:mm strings minus break minutes. */
function calcHours(startTime: string, endTime: string, breakMinutes: number): number {
  if (!startTime || !endTime) return 0;
  const [sh, sm] = startTime.split(':').map(Number);
  const [eh, em] = endTime.split(':').map(Number);
  const startMins = sh * 60 + sm;
  const endMins = eh * 60 + em;
  const durationMins = endMins - startMins - breakMinutes;
  return Math.max(0, Math.round((durationMins / 60) * 100) / 100);
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
}

function buildEmptyDayEntry(date: Date, label: string): DayEntry {
  return {
    date: date.toISOString().split('T')[0],
    dayLabel: label,
    startTime: '',
    endTime: '',
    breakMinutes: 0,
    hoursWorked: 0,
    costCode: '',
    costCodeDescription: '',
    notes: '',
  };
}

// ============================================================================
// COMPONENT
// ============================================================================

const TimesheetDrawer: React.FC<TimesheetDrawerProps> = ({
  isOpen,
  onClose,
  timesheet,
  mode,
  onSuccess,
  teamMembers = [],
}) => {
  const projectId = useCurrentProjectId();
  const toast = useToast();

  const [createTimesheet, { isLoading: isCreating }] = useCreateTimesheetMutation();
  const [updateTimesheet, { isLoading: isUpdating }] = useUpdateTimesheetMutation();
  const [createCostCode] = useCreateCostCodeMutation();
  const [sendInvitations] = useSendInvitationsMutation();

  const { data: costCodesData } = useGetCostCodesQuery({ isActive: true, limit: 500 });

  // Fetch team members and invitations directly — self-sufficient, not relying on parent prop
  const { data: teamQueryData } = useGetProjectTeamQuery(
    { projectId: projectId!, limit: 200 },
    { skip: !projectId || !isOpen }
  );
  const { data: invitationsData } = useGetProjectInvitationsQuery(
    { projectId: projectId! },
    { skip: !projectId || !isOpen }
  );

  // ============================================================================
  // STATE
  // ============================================================================

  const [selectedEmployeeId, setSelectedEmployeeId] = useState('');
  const [selectedEmployeeName, setSelectedEmployeeName] = useState('');
  const [employeeRole, setEmployeeRole] = useState('');
  const [regularRate, setRegularRate] = useState<number>(0);
  const [overtimeRate, setOvertimeRate] = useState<number>(0);

  // Week selection — store the Monday date
  const [weekMonday, setWeekMonday] = useState<Date>(getMondayOfWeek(new Date()));

  // 7-day grid: one entry per day
  const [dayEntries, setDayEntries] = useState<DayEntry[]>([]);

  // Locally-added employees (available immediately, before invitation acceptance)
  const [localEmployees, setLocalEmployees] = useState<
    { _id: string; name: string; email: string; role?: string }[]
  >([]);

  // Add Employee modal
  const [showAddEmployee, setShowAddEmployee] = useState(false);
  const [newEmployeeFirstName, setNewEmployeeFirstName] = useState('');
  const [newEmployeeLastName, setNewEmployeeLastName] = useState('');
  const [newEmployeeEmail, setNewEmployeeEmail] = useState('');
  const [newEmployeeRole, setNewEmployeeRole] = useState('');
  const [isAddingEmployee, setIsAddingEmployee] = useState(false);

  const isViewMode = mode === 'view';
  const isSaving = isCreating || isUpdating;

  // ============================================================================
  // DERIVED DATA
  // ============================================================================

  const weekDays = useMemo(() => buildWeekDays(weekMonday), [weekMonday]);

  const costCodeOptions = useMemo(() => {
    const codes: CostCode[] = costCodesData?.data?.costCodes || [];
    return codes.map(c => ({
      value: c.code,
      label: `${c.code} — ${c.description}`,
      description: c.description,
    }));
  }, [costCodesData]);

  const teamMemberOptions = useMemo(() => {
    const seen = new Set<string>();
    const allMembers: { _id: string; name: string; email: string }[] = [];

    const addMember = (id: string, name: string, email: string) => {
      const key = email.toLowerCase();
      if (seen.has(key)) return;
      seen.add(key);
      allMembers.push({ _id: id, name, email });
    };

    // 1. Team members from direct query (most reliable)
    const serverMembers = teamQueryData?.data?.data || [];
    for (const m of serverMembers) {
      if (m.user) {
        const fullName =
          `${m.user.firstName || ''} ${m.user.lastName || ''}`.trim() || m.user.email;
        addMember(m.userId, fullName, m.user.email);
      }
    }

    // 2. Team members from parent prop (fallback)
    for (const m of teamMembers) {
      addMember(m._id, m.name, m.email);
    }

    // 3. Pending invitations (people invited but haven't accepted yet)
    const invitations = invitationsData?.data || [];
    for (const inv of invitations) {
      if (inv.status === 'pending') {
        addMember(`inv_${inv.id}`, inv.email, inv.email);
      }
    }

    // 4. Locally-added employees (instant feedback before API refresh)
    for (const local of localEmployees) {
      addMember(local._id, local.name, local.email);
    }

    return allMembers.map(m => ({
      value: m._id,
      label: m.name,
      description: m.email,
    }));
  }, [teamQueryData, teamMembers, invitationsData, localEmployees]);

  // Totals
  const totals = useMemo(() => {
    let regularHours = 0;
    let overtimeHours = 0;
    let totalHours = 0;

    dayEntries.forEach(entry => {
      totalHours += entry.hoursWorked;
      // Simple 8hr daily threshold for UI display (backend applies full rules)
      if (entry.hoursWorked > 8) {
        regularHours += 8;
        overtimeHours += entry.hoursWorked - 8;
      } else {
        regularHours += entry.hoursWorked;
      }
    });

    const regularCost = regularHours * (regularRate || 0);
    const overtimeCost = overtimeHours * (overtimeRate || 0);
    const totalCost = regularCost + overtimeCost;

    return { regularHours, overtimeHours, totalHours, regularCost, overtimeCost, totalCost };
  }, [dayEntries, regularRate, overtimeRate]);

  // ============================================================================
  // EFFECTS
  // ============================================================================

  // Initialize grid when week changes or timesheet changes
  useEffect(() => {
    if (!isOpen) return;

    const initialEntries = weekDays.map(({ date, label }) => {
      if (timesheet) {
        const existingEntry = timesheet.entries.find(e => {
          const entryDate = new Date(e.date).toISOString().split('T')[0];
          return entryDate === date.toISOString().split('T')[0];
        });
        if (existingEntry) {
          return {
            date: date.toISOString().split('T')[0],
            dayLabel: label,
            startTime: existingEntry.startTime || '',
            endTime: existingEntry.endTime || '',
            breakMinutes: existingEntry.breakMinutes || 0,
            hoursWorked: existingEntry.hoursWorked || 0,
            costCode: existingEntry.costCode || '',
            costCodeDescription: existingEntry.costCodeDescription || '',
            notes: existingEntry.notes || '',
          };
        }
      }
      return buildEmptyDayEntry(date, label);
    });

    setDayEntries(initialEntries);
  }, [isOpen, weekDays, timesheet]);

  // Populate form from existing timesheet
  useEffect(() => {
    if (!isOpen) return;

    if (timesheet) {
      const employeeId =
        typeof timesheet.employeeId === 'string' ? timesheet.employeeId : timesheet.employeeId._id;
      setSelectedEmployeeId(employeeId);
      setSelectedEmployeeName(timesheet.employeeName);
      setEmployeeRole(timesheet.employeeRole || '');
      setRegularRate(timesheet.regularRate);
      setOvertimeRate(timesheet.overtimeRate);
      setWeekMonday(getMondayOfWeek(new Date(timesheet.weekStartDate)));
    } else {
      setSelectedEmployeeId('');
      setSelectedEmployeeName('');
      setEmployeeRole('');
      setRegularRate(0);
      setOvertimeRate(0);
      setWeekMonday(getMondayOfWeek(new Date()));
    }
  }, [isOpen, timesheet]);

  // ============================================================================
  // HANDLERS
  // ============================================================================

  const handleWeekChange = (dateStr: string | null) => {
    if (dateStr) {
      setWeekMonday(getMondayOfWeek(new Date(dateStr)));
    }
  };

  const handleEmployeeChange = (employeeId: string) => {
    // Search the merged options list (includes server team, invitations, and local additions)
    const option = teamMemberOptions.find(o => o.value === employeeId);
    setSelectedEmployeeId(employeeId);
    setSelectedEmployeeName(option?.label || '');
    // Try to find the role from server team data or local employees
    const serverMember = teamMembers.find(m => m._id === employeeId);
    const localMember = localEmployees.find(m => m._id === employeeId);
    setEmployeeRole(serverMember?.role || localMember?.role || '');
  };

  const handleCreateCostCode = async (searchTerm: string, dayIndex: number) => {
    try {
      const result = await createCostCode({
        code: searchTerm.toUpperCase().replace(/\s+/g, '-'),
        description: searchTerm,
        division: 'General',
        level: 1,
      }).unwrap();
      if (result.data?.code) {
        updateDayEntry(dayIndex, 'costCode', result.data.code);
      }
      toast.success(`Cost code "${searchTerm}" created`);
    } catch {
      toast.error('Failed to create cost code');
    }
  };

  const handleAddEmployee = async () => {
    if (!projectId || !newEmployeeEmail.trim()) {
      toast.error('Email is required');
      return;
    }
    setIsAddingEmployee(true);
    try {
      const result = await sendInvitations({
        projectId,
        emails: [newEmployeeEmail.trim()],
        role: newEmployeeRole || 'contractor',
        permissions: [],
        emailStatuses: {
          [newEmployeeEmail.trim()]: {
            exists: false,
            userData: {
              firstName: newEmployeeFirstName.trim() || undefined,
              lastName: newEmployeeLastName.trim() || undefined,
            },
          },
        },
      }).unwrap();
      const fullName = `${newEmployeeFirstName.trim()} ${newEmployeeLastName.trim()}`.trim();
      const displayName = fullName || newEmployeeEmail.trim();
      const email = newEmployeeEmail.trim();

      // Generate a stable local ID from the invitation response or email
      const successEntry = result.data?.success?.[0];
      const newId = successEntry?.invitationId || `local_${email}`;

      // Add to local employees so they appear in the dropdown immediately
      setLocalEmployees(prev => [
        ...prev,
        { _id: newId, name: displayName, email, role: newEmployeeRole },
      ]);

      // Auto-select the new employee
      setSelectedEmployeeId(newId);
      setSelectedEmployeeName(displayName);
      setEmployeeRole(newEmployeeRole);

      setShowAddEmployee(false);
      setNewEmployeeFirstName('');
      setNewEmployeeLastName('');
      setNewEmployeeEmail('');
      setNewEmployeeRole('');
      toast.success('Employee invited and selected');
    } catch {
      toast.error('Failed to add employee');
    } finally {
      setIsAddingEmployee(false);
    }
  };

  const updateDayEntry = (index: number, field: keyof DayEntry, value: string | number) => {
    setDayEntries(prev => {
      const updated = [...prev];
      const entry = { ...updated[index], [field]: value };

      // Auto-calculate hours when times change
      if (field === 'startTime' || field === 'endTime' || field === 'breakMinutes') {
        entry.hoursWorked = calcHours(
          field === 'startTime' ? (value as string) : entry.startTime,
          field === 'endTime' ? (value as string) : entry.endTime,
          field === 'breakMinutes' ? (value as number) : entry.breakMinutes
        );
      }

      // Sync costCodeDescription when costCode changes
      if (field === 'costCode') {
        const option = costCodeOptions.find(o => o.value === value);
        entry.costCodeDescription = option?.description || '';
      }

      updated[index] = entry;
      return updated;
    });
  };

  const handleSubmit = async () => {
    if (!projectId) {
      toast.error('No project selected');
      return;
    }

    if (!selectedEmployeeId && !selectedEmployeeName) {
      toast.error('Please select an employee');
      return;
    }

    const activeEntries = dayEntries.filter(e => e.hoursWorked > 0 || e.startTime);

    if (activeEntries.length === 0) {
      toast.error('Please add at least one time entry');
      return;
    }

    const weekEnd = new Date(weekMonday);
    weekEnd.setDate(weekMonday.getDate() + 6);

    const entries: CreateTimesheetEntryRequest[] = activeEntries.map(e => ({
      date: e.date,
      startTime: e.startTime || undefined,
      endTime: e.endTime || undefined,
      breakMinutes: e.breakMinutes,
      hoursWorked: e.hoursWorked,
      costCode: e.costCode,
      costCodeDescription: e.costCodeDescription || undefined,
      notes: e.notes || undefined,
    }));

    try {
      if (mode === 'edit' && timesheet) {
        await updateTimesheet({
          projectId,
          timesheetId: timesheet._id,
          data: {
            employeeName: selectedEmployeeName,
            employeeRole: employeeRole || undefined,
            entries,
            regularRate,
            overtimeRate,
          },
        }).unwrap();
        toast.success('Timesheet updated successfully');
      } else {
        await createTimesheet({
          projectId,
          data: {
            employeeId: selectedEmployeeId,
            employeeName: selectedEmployeeName,
            employeeRole: employeeRole || undefined,
            weekStartDate: weekMonday.toISOString().split('T')[0],
            weekEndDate: weekEnd.toISOString().split('T')[0],
            entries,
            regularRate,
            overtimeRate,
          },
        }).unwrap();
        toast.success('Timesheet created successfully');
      }

      onSuccess?.();
      onClose();
    } catch {
      toast.error('Failed to save timesheet');
    }
  };

  // ============================================================================
  // RENDER
  // ============================================================================

  if (!isOpen) return null;

  return createPortal(
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[90]" onClick={onClose} />

      {/* Drawer — 90% width for the complex weekly grid */}
      <div className="fixed top-6 right-6 bottom-6 z-[100] inset-6 animate-in slide-in-from-right">
        <div className="bg-white rounded-2xl shadow-2xl h-full flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-8 py-5 border-b border-gray-200 flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-lg bg-brand-50 flex items-center justify-center">
                <Clock className="h-5 w-5 text-brand-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  {mode === 'create'
                    ? 'New Weekly Timesheet'
                    : mode === 'edit'
                      ? 'Edit Timesheet'
                      : 'View Timesheet'}
                </h2>
                <p className="text-xs text-gray-500 mt-0.5">
                  {isViewMode ? 'Read-only view' : 'Fill in daily hours for the selected week'}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="h-8 w-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto px-8 py-6 space-y-6">
            {/* Top section: Employee + Week */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Employee */}
              <div className="md:col-span-1">
                {!isViewMode ? (
                  <SearchableDropdown
                    label="Employee"
                    placeholder="Select employee..."
                    options={teamMemberOptions}
                    value={selectedEmployeeId}
                    onChange={handleEmployeeChange}
                    emptyMessage="No team members found. Type to add."
                    creatable
                    onCreateNew={() => setShowAddEmployee(true)}
                    required
                  />
                ) : (
                  <div>
                    <label className="block text-xs text-gray-500 mb-1.5 font-medium">
                      Employee
                    </label>
                    <div className="h-10 px-3 border border-gray-300 rounded-lg flex items-center text-sm text-gray-700 bg-gray-50">
                      {selectedEmployeeName || '—'}
                    </div>
                  </div>
                )}
              </div>

              {/* Role */}
              <div>
                <Input
                  label="Role / Trade"
                  placeholder="e.g. Carpenter, Electrician"
                  value={employeeRole}
                  onChange={e => setEmployeeRole(e.target.value)}
                  disabled={isViewMode}
                />
              </div>

              {/* Week selector */}
              <div>
                <DatePicker
                  label="Week of (any day)"
                  value={weekMonday.toISOString().split('T')[0]}
                  onChange={handleWeekChange}
                  disabled={isViewMode || mode === 'edit'}
                  hint={`Week: ${weekMonday.toLocaleDateString()} – ${new Date(weekMonday.getTime() + 6 * 86400000).toLocaleDateString()}`}
                />
              </div>
            </div>

            {/* Rates row */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <NumberInput
                  label="Regular Rate ($/hr)"
                  value={regularRate}
                  onChange={(val: number) => setRegularRate(val)}
                  min={0}
                  disabled={isViewMode}
                />
              </div>
              <div>
                <NumberInput
                  label="OT Rate ($/hr)"
                  value={overtimeRate}
                  onChange={(val: number) => setOvertimeRate(val)}
                  min={0}
                  disabled={isViewMode}
                />
              </div>
            </div>

            {/* Weekly Grid */}
            <div>
              <h3 className="text-sm font-semibold text-gray-800 mb-3">Daily Time Entries</h3>
              <div className="border border-gray-200 rounded-xl overflow-visible">
                {/* Grid header */}
                <div className={`grid ${isViewMode ? 'grid-cols-[80px_140px_140px_70px_70px_1fr_1fr]' : 'grid-cols-[80px_140px_140px_70px_70px_1fr_1fr_40px]'} bg-gray-50 border-b border-gray-200 text-xs font-medium text-gray-500 uppercase tracking-wide rounded-t-xl`}>
                  <div className="px-3 py-2.5">Day</div>
                  <div className="px-3 py-2.5">Start</div>
                  <div className="px-3 py-2.5">End</div>
                  <div className="px-3 py-2.5">Break</div>
                  <div className="px-3 py-2.5">Hours</div>
                  <div className="px-3 py-2.5">Cost Code</div>
                  <div className="px-3 py-2.5">Notes</div>
                  {!isViewMode && <div className="px-3 py-2.5" />}
                </div>

                {/* Grid rows */}
                {dayEntries.map((entry, idx) => {
                  const isWeekend = idx >= 5; // Sat, Sun
                  const hasHours = entry.hoursWorked > 0;
                  const isOT = hasHours && entry.hoursWorked > 8;

                  return (
                    <div
                      key={entry.date}
                      style={{ zIndex: 7 - idx }}
                      className={[
                        `relative grid ${isViewMode ? 'grid-cols-[80px_140px_140px_70px_70px_1fr_1fr]' : 'grid-cols-[80px_140px_140px_70px_70px_1fr_1fr_40px]'} border-b border-gray-100 last:border-b-0`,
                        isWeekend ? 'bg-gray-50/50' : 'bg-white',
                        hasHours ? 'bg-blue-50/20' : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                    >
                      {/* Day label */}
                      <div className="px-3 py-2 flex items-center">
                        <span
                          className={`text-sm font-medium ${isWeekend ? 'text-gray-400' : 'text-gray-700'}`}
                        >
                          {entry.dayLabel}
                        </span>
                      </div>

                      {/* Start time */}
                      <div className="px-1 py-1.5 flex items-center">
                        <TimePicker
                          value={entry.startTime}
                          onChange={val => updateDayEntry(idx, 'startTime', val)}
                          disabled={isViewMode}
                          compact
                        />
                      </div>

                      {/* End time */}
                      <div className="px-1 py-1.5 flex items-center">
                        <TimePicker
                          value={entry.endTime}
                          onChange={val => updateDayEntry(idx, 'endTime', val)}
                          disabled={isViewMode}
                          compact
                        />
                      </div>

                      {/* Break minutes */}
                      <div className="px-1 py-1.5 flex items-center">
                        <Input
                          type="number"
                          value={String(entry.breakMinutes)}
                          onChange={e =>
                            updateDayEntry(idx, 'breakMinutes', parseInt(e.target.value) || 0)
                          }
                          min={0}
                          max={480}
                          disabled={isViewMode}
                          className="h-9"
                        />
                      </div>

                      {/* Hours worked — readonly, auto-calculated */}
                      <div className="px-3 py-2 flex items-center">
                        <span
                          className={`text-sm font-semibold ${
                            isOT ? 'text-amber-600' : hasHours ? 'text-gray-900' : 'text-gray-300'
                          }`}
                        >
                          {hasHours ? entry.hoursWorked.toFixed(2) : '—'}
                          {isOT && (
                            <span className="ml-1 text-xs font-normal text-amber-500">OT</span>
                          )}
                        </span>
                      </div>

                      {/* Cost code */}
                      <div className="px-1 py-1.5 flex items-center">
                        {isViewMode ? (
                          <span className="text-sm text-gray-700">{entry.costCode || '—'}</span>
                        ) : (
                          <SearchableDropdown
                            placeholder="Select..."
                            options={costCodeOptions}
                            value={entry.costCode}
                            onChange={val => updateDayEntry(idx, 'costCode', val)}
                            creatable
                            onCreateNew={term => handleCreateCostCode(term, idx)}
                          />
                        )}
                      </div>

                      {/* Notes */}
                      <div className="px-1 py-1.5 flex items-center">
                        {isViewMode ? (
                          <span className="text-sm text-gray-500">{entry.notes || '—'}</span>
                        ) : (
                          <Input
                            value={entry.notes}
                            onChange={e => updateDayEntry(idx, 'notes', e.target.value)}
                            placeholder="Optional notes"
                            className="h-9"
                          />
                        )}
                      </div>

                      {/* Clear row action */}
                      {!isViewMode && (
                        <div className="px-1 py-1.5 flex items-center justify-center">
                          {hasHours && (
                            <button
                              type="button"
                              onClick={() => {
                                const emptyEntry = buildEmptyDayEntry(
                                  new Date(entry.date),
                                  entry.dayLabel
                                );
                                setDayEntries(prev => {
                                  const updated = [...prev];
                                  updated[idx] = emptyEntry;
                                  return updated;
                                });
                              }}
                              className="h-7 w-7 flex items-center justify-center rounded-lg text-gray-300 hover:text-red-400 hover:bg-red-50 transition-colors"
                              title="Clear day"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Running Totals */}
            <div className="bg-gray-50 rounded-xl p-5 relative z-0">
              <h3 className="text-sm font-semibold text-gray-700 mb-4">Weekly Totals</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                <div className="text-center">
                  <p className="text-xs text-gray-500 mb-1">Regular Hours</p>
                  <p className="text-lg font-bold text-gray-900">
                    {totals.regularHours.toFixed(2)}
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-gray-500 mb-1">OT Hours</p>
                  <p
                    className={`text-lg font-bold ${totals.overtimeHours > 0 ? 'text-amber-600' : 'text-gray-900'}`}
                  >
                    {totals.overtimeHours.toFixed(2)}
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-gray-500 mb-1">Total Hours</p>
                  <p className="text-lg font-bold text-brand-600">{totals.totalHours.toFixed(2)}</p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-gray-500 mb-1">Regular Cost</p>
                  <p className="text-lg font-bold text-gray-900">
                    {formatCurrency(totals.regularCost)}
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-gray-500 mb-1">OT Cost</p>
                  <p
                    className={`text-lg font-bold ${totals.overtimeCost > 0 ? 'text-amber-600' : 'text-gray-900'}`}
                  >
                    {formatCurrency(totals.overtimeCost)}
                  </p>
                </div>
                <div className="text-center bg-white rounded-lg p-2 shadow-sm border border-gray-200">
                  <p className="text-xs text-gray-500 mb-1">Total Cost</p>
                  <p className="text-lg font-bold text-green-700">
                    {formatCurrency(totals.totalCost)}
                  </p>
                </div>
              </div>
            </div>

            {/* Validation hint */}
            {!selectedEmployeeId && !selectedEmployeeName && !isViewMode && (
              <div className="flex items-center gap-2 text-xs text-amber-600 bg-amber-50 px-4 py-3 rounded-lg">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                <span>Select an employee to save the timesheet.</span>
              </div>
            )}
          </div>

          {/* Footer */}
          {!isViewMode && (
            <div className="border-t border-gray-200 px-8 py-5 bg-gray-50 flex items-center justify-end gap-3 flex-shrink-0 rounded-b-2xl">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                disabled={isSaving}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={isSaving || (!selectedEmployeeId && !selectedEmployeeName)}
                className="px-5 py-2 text-sm font-medium text-white bg-brand-600 hover:bg-brand-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSaving ? 'Saving...' : mode === 'edit' ? 'Update Timesheet' : 'Create Timesheet'}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Add Employee Modal */}
      {showAddEmployee && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => setShowAddEmployee(false)}
          />
          <div className="relative bg-white rounded-2xl shadow-2xl p-6 w-full max-w-md mx-4">
            <h3 className="text-base font-semibold text-gray-900 mb-1">Add Employee</h3>
            <p className="text-sm text-gray-500 mb-4">
              Invite a new team member to this project.
            </p>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <Input
                  label="First Name"
                  placeholder="First name"
                  value={newEmployeeFirstName}
                  onChange={e => setNewEmployeeFirstName(e.target.value)}
                />
                <Input
                  label="Last Name"
                  placeholder="Last name"
                  value={newEmployeeLastName}
                  onChange={e => setNewEmployeeLastName(e.target.value)}
                />
              </div>
              <Input
                label="Email"
                placeholder="employee@company.com"
                value={newEmployeeEmail}
                onChange={e => setNewEmployeeEmail(e.target.value)}
                required
              />
              <SearchableDropdown
                label="Role"
                placeholder="Select role..."
                options={TEAM_ROLE_OPTIONS}
                value={newEmployeeRole}
                onChange={val => setNewEmployeeRole(val)}
                required
              />
            </div>
            <div className="flex justify-end gap-3 mt-5">
              <button
                type="button"
                onClick={() => setShowAddEmployee(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                disabled={isAddingEmployee}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleAddEmployee}
                disabled={isAddingEmployee || !newEmployeeEmail.trim() || !newEmployeeRole}
                className="px-4 py-2 text-sm font-medium text-white bg-brand-600 hover:bg-brand-700 rounded-lg disabled:opacity-50"
              >
                {isAddingEmployee ? 'Adding...' : 'Add & Select'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>,
    document.body
  );
};

export default TimesheetDrawer;
